import { useEffect, useState, type FormEvent } from 'react'
import { CircleCheck, FileText, PanelRightOpen } from 'lucide-react'
import { PhotoType } from '@numeo/types-core'
import type { TmsLoadDocumentCheckState, TmsLoadDocumentFile, TmsLoadDocumentFinding, TmsLoadDocumentView } from '@numeo/types-main-web-app'
import { safeHref } from '@/components/agent-markdown/renderAgentInline'
import { Button } from '@/components/ds2'
import { ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import { checkReadFile, usePodCheckWatch } from '../../hooks/usePodCheckWatch'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { refreshInvoicingRow } from '../invoicing/review/refreshRow'
import { UploadButton } from '../uploads/UploadButton'
import { formatDate } from './format'
import { useInvoiceReview } from '../invoicing/review/reviewStore'

const NAME = { pod: 'POD', bol: 'BOL' } as const

const CHECK_WORDS: Record<Exclude<TmsLoadDocumentCheckState, 'checked'>, string> = {
    none: 'No file is on the load yet.',
    running: 'The automatic check is still running.',
    filed_by_hand: 'Filed by hand: no automatic check ran.',
}

/** The chat message that asks the agent to accept the document; the agent's accept card then asks the user to confirm (never a direct write). */
const acceptMessage = (document: TmsLoadDocumentView, reason: string): string =>
    `Accept the ${NAME[document.document]}${document.stop_number ? ` of stop ${document.stop_number}` : ''} for #${document.load_number} — reason: ${reason.trim()}`

/**
 * One file as the board's Paperwork tab opens it: an image as a thumbnail, a PDF as a link, both to the stored link in a new tab.
 * The link comes from a tool result, so it is held to the same rule as a link in the agent's text (http, https, mailto): any
 * other value is shown as the file's name only, with no link and no image loaded.
 */
const FileTile = ({ file }: { file: TmsLoadDocumentFile }) => {
    const url = safeHref(file.url)
    const tile = (
        <>
            {file.kind === 'image' && url ? (
                <img src={url} alt={file.name} className="h-20 w-20 rounded-xl bg-default object-cover" />
            ) : (
                <span className="flex h-20 w-20 items-center justify-center rounded-xl bg-default">
                    <FileText className="h-8 w-8 text-foreground-muted" aria-hidden />
                </span>
            )}
            <span className="max-w-20 truncate">{file.name}</span>
        </>
    )
    return url ? (
        <a href={url} target="_blank" rel="noreferrer" title={file.name} className="flex flex-col items-center gap-1 text-xs text-accent hover:underline">
            {tile}
        </a>
    ) : (
        <span title={file.name} className="flex flex-col items-center gap-1 text-xs text-foreground-muted">
            {tile}
        </span>
    )
}

/** One point the check failed the document on, with what it read on the document beside what the load holds. */
const Finding = ({ finding, name }: { finding: TmsLoadDocumentFinding; name: string }) => (
    <li>
        {finding.message}
        {(finding.pod_value !== undefined || finding.load_value !== undefined) && (
            <span className="text-foreground-muted">
                {' '}
                ({name}: {finding.pod_value ?? '—'} · Load: {finding.load_value ?? '—'})
            </span>
        )}
    </li>
)

/** What the automatic check says: its state, or each finding, or that it passed. */
const CheckSummary = ({ document }: { document: TmsLoadDocumentView }) => {
    const { check } = document
    const name = NAME[document.document]
    if (check.state !== 'checked') return <p className="text-xs text-foreground-muted">{CHECK_WORDS[check.state]}</p>
    return (
        <div className="flex flex-col gap-1 text-xs text-foreground">
            {check.findings.length ? (
                <>
                    <p className="font-medium">The check found:</p>
                    <ul className="flex list-disc flex-col gap-1 pl-5" aria-label="Check findings">
                        {check.findings.map((finding, i) => (
                            <Finding key={`${finding.code}-${i}`} finding={finding} name={name} />
                        ))}
                    </ul>
                </>
            ) : (
                <p>{check.verified === false ? 'The check did not pass, and names no failed point: review the file.' : 'The check found no issue.'}</p>
            )}
            {check.reported && <p className="text-foreground-muted">An issue with this check was reported.</p>}
        </div>
    )
}

/** The reason the board asks for before accepting, then the message that asks the agent to accept it. Sends once: `onSent` closes it. */
const AcceptForm = ({ document, onSend, onSent }: { document: TmsLoadDocumentView; onSend: (message: string) => void; onSent: (sent: boolean) => void }) => {
    const [reason, setReason] = useState('')
    const submit = (event: FormEvent) => {
        event.preventDefault()
        if (!reason.trim()) return
        onSend(acceptMessage(document, reason))
        onSent(true)
    }
    return (
        <form className="flex flex-wrap items-center gap-2" onSubmit={submit}>
            <input
                aria-label="Reason for accepting"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why accept it?"
                autoFocus
                className="min-w-40 flex-1 rounded-xl bg-surface px-3 py-1.5 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent"
            />
            <Button size="sm" type="submit" disabled={!reason.trim()}>
                Send
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => onSent(false)}>
                Cancel
            </Button>
        </form>
    )
}

interface PodPreviewCardProps {
    document: TmsLoadDocumentView
    onSend: (message: string) => void
}

/**
 * A `tms_load_documents.view` answer: the load's POD (or a stop's BOL) with its files, what the automatic check found and whether it
 * is accepted. "Accept" asks for the reason and sends it to the agent, whose accept card asks the user to confirm; a POD can be
 * uploaded again from here. The card is a snapshot: Accept is offered only while the load's latest invoicing row still shows the POD
 * unaccepted, and a finished check refreshes that row (the table and an open review drawer follow it) without a chat message.
 */
export const PodPreviewCard = ({ document, onSend }: PodPreviewCardProps) => {
    const [accepting, setAccepting] = useState(false)
    // Once the accept is asked for, the agent's approval card carries it on; the button is not offered again.
    const [sent, setSent] = useState(false)
    const openReview = useInvoiceReview((state) => state.open)
    const latest = useInvoicingRowsStore((s) => s.rows[document.load_number])
    // The row's POD state is read now, so an accept made elsewhere (the board, the drawer) is not offered again on this card.
    useEffect(() => {
        void refreshInvoicingRow(document.load_number)
    }, [document.load_number])
    // The check's state is the latest read's, not the card's snapshot: a check that was still running when the card was saved
    // finishes on the board, and the watch (or one read now, when the watch is off) says so.
    const [checkFinished, setCheckFinished] = useState(false)
    const newest = document.files[0]?.url
    usePodCheckWatch(document, true, () => {
        setCheckFinished(true)
        void refreshInvoicingRow(document.load_number)
    })
    useEffect(() => {
        if (document.check.state !== 'running' || !newest) return
        let live = true
        ongoingLoadsApi
            .getOngoingLoadById(document.load_id)
            .then((load) => {
                if (live && checkReadFile(load, newest)) setCheckFinished(true)
            })
            .catch(() => undefined)
        return () => {
            live = false
        }
    }, [document.check.state, document.load_id, newest])
    const name = NAME[document.document]
    const where = document.stop_number ? ` (stop ${document.stop_number})` : ''
    const acceptedNow = document.accepted || latest?.documents?.pod === 'accepted'
    // The board offers Accept once a file is in and its check is not still running.
    const checkRunning = document.check.state === 'running' && !checkFinished
    const canAccept = !sent && !acceptedNow && document.files.length > 0 && !checkRunning
    return (
        <article className="flex flex-col gap-3 rounded-xl bg-default p-3" aria-label={`${name} for #${document.load_number}`}>
            <header className="flex items-center gap-2 text-sm font-semibold text-foreground">
                {name}
                {where} for #{document.load_number}
                {document.broker && <span className="font-normal text-foreground-muted">{document.broker}</span>}
            </header>
            {document.files.length > 0 && (
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap gap-2">
                        {document.files.map((file) => (
                            <FileTile key={file.url} file={file} />
                        ))}
                    </div>
                    {document.files_count > document.files.length && (
                        <p className="text-xs text-foreground-muted">
                            Showing the newest {document.files.length} of {document.files_count} files.
                        </p>
                    )}
                </div>
            )}
            <CheckSummary document={document} />
            {document.accepted && (
                <p className="flex items-start gap-1 text-xs text-success">
                    <CircleCheck className="h-4 w-4 shrink-0" aria-hidden />
                    <span>
                        Accepted{document.accepted_by ? ` by ${document.accepted_by}` : ''}
                        {document.accepted_at ? ` on ${formatDate(document.accepted_at)}` : ''}
                        {document.accepted_reason ? ` — ${document.accepted_reason}` : ''}
                    </span>
                </p>
            )}
            {accepting ? (
                <AcceptForm
                    document={document}
                    onSend={onSend}
                    onSent={(done) => {
                        setAccepting(false)
                        setSent(done)
                    }}
                />
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    {sent && <span className="text-xs text-foreground-muted">Accept sent to the agent.</span>}
                    {canAccept && (
                        <Button size="sm" onClick={() => setAccepting(true)}>
                            Accept {name}
                        </Button>
                    )}
                    {/* The POD's own view in the review drawer, the page's one right-hand panel; a BOL's files are the tiles above. */}
                    {document.files.length > 0 && document.document === 'pod' && (
                        <Button
                            size="sm"
                            variant="secondary"
                            prefix={<PanelRightOpen className="h-3.5 w-3.5" />}
                            onClick={() => openReview(document.load_number, 'document', document.load_id)}
                        >
                            Open paperwork
                        </Button>
                    )}
                    {document.document === 'pod' && (
                        <UploadButton
                            loadNumber={document.load_number}
                            loadId={document.load_id}
                            type={PhotoType.POD}
                            label={document.files.length ? 'Upload another POD' : 'Upload POD'}
                        />
                    )}
                </div>
            )}
        </article>
    )
}
